import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shouldShowFreeBanner } from '@/access/access';
import { useDetailViewsQuota } from '@/access/quotaStore';
import { useAccess } from '@/access/useAccess';
import { useAuth } from '@/auth/AuthProvider';
import { usePaywall } from '@/billing/PaywallContext';
import { usePaywallPrice } from '@/billing/usePaywallPrice';
import { useVideoDetail } from '@/features/video/VideoDetailContext';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './FreeQuotaBadge.module.css';

/**
 * Счётчик бесплатного тарифа: сколько детальных просмотров осталось сегодня.
 * Виден только там, где виден платный слой (как баннер CAT-13); на полотне и
 * публичной странице не показывается — там свои углы заняты. Остаток кончился —
 * рядом цена плана, который окно оплаты выберет само, пока панель разбора закрыта
 * (в панели цену несёт строка последнего просмотра).
 */
const BADGE_HEIGHT_VAR = '--app-quota-badge-h';

/**
 * Высота видимой плашки — CSS-переменная на <html>: мобильные страницы добирают её
 * к нижнему отступу (app-tokens.css), и последний ряд не прячется под плашкой.
 */
function useBadgeRoom(badge: HTMLElement | null) {
  useEffect(() => {
    if (!badge) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty(BADGE_HEIGHT_VAR, `${badge.offsetHeight}px`);
    apply();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(badge);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BADGE_HEIGHT_VAR);
    };
  }, [badge]);
}

export function FreeQuotaBadge() {
  const access = useAccess();
  const { user } = useAuth();
  const t = useT();
  const { pathname } = useLocation();
  const { openPaywall } = usePaywall();
  const live = useDetailViewsQuota();
  const quota = live ?? user?.quota?.detail_views ?? null;
  const { videoId } = useVideoDetail();
  const paywallPrice = usePaywallPrice();
  const [badge, setBadge] = useState<HTMLButtonElement | null>(null);
  useBadgeRoom(badge);

  if (!quota || !shouldShowFreeBanner(access, user)) return null;
  if (/^\/canvases\/\d+/.test(pathname) || pathname.startsWith('/shared')) return null;

  const price = quota.remaining === 0 && videoId === null ? paywallPrice : null;
  const label = `${t('quota.detailViews')}: ${t('quota.remaining', { remaining: quota.remaining, limit: quota.limit })}`;

  return (
    <button
      ref={setBadge}
      type="button"
      className={cx(styles.badge, quota.remaining === 0 && styles.badgeEmpty)}
      title={t('quota.badgeTitle')}
      aria-label={price ? `${label} · ${price}` : label}
      data-quota-badge
      onClick={() =>
        openPaywall({ trigger: 'intent', source: 'quota_badge', reason: 'detail_quota' })
      }
    >
      <span>{t('quota.detailViews')}</span>
      <span className={styles.value}>
        {t('quota.remaining', { remaining: quota.remaining, limit: quota.limit })}
      </span>
      {price ? <span className={styles.price}>{price}</span> : null}
    </button>
  );
}
